/**
 * Ícones SVG para marcadores no mapa FTTH (pin CTO, reserva, CEO).
 * viewBox pin: 40×48 — ponta em (20, 48).
 */

export const CAIXA_DEFAULT_ICON_ID = 'icon01';

export const MAP_PIN_VIEW_WIDTH = 40;
export const MAP_PIN_VIEW_HEIGHT = 48;
export const MAP_PIN_DISPLAY_WIDTH = 28;
export const MAP_PIN_DISPLAY_HEIGHT = 33.6;
export const MAP_PIN_ANCHOR_X = 14;
export const MAP_PIN_ANCHOR_Y = 33.6;

/** Centro da bola branca no viewBox (origem da label de portas). */
export const MAP_PIN_LABEL_CENTER_X = 20;
export const MAP_PIN_LABEL_CENTER_Y = 17.5;

/** CTO: pin menor que reserva. */
const CTO_PIN_SCALE = 0.77;
export const CTO_PIN_DISPLAY_WIDTH = MAP_PIN_DISPLAY_WIDTH * CTO_PIN_SCALE;
export const CTO_PIN_DISPLAY_HEIGHT = MAP_PIN_DISPLAY_HEIGHT * CTO_PIN_SCALE;
export const CTO_PIN_ANCHOR_X = MAP_PIN_ANCHOR_X * CTO_PIN_SCALE;
export const CTO_PIN_ANCHOR_Y = MAP_PIN_ANCHOR_Y * CTO_PIN_SCALE;

/** CEO: mesma largura do pin CTO, altura ampliada. */
const CEO_MAP_HEIGHT_SCALE = 1.5;
export const CEO_MAP_DISPLAY_WIDTH = CTO_PIN_DISPLAY_WIDTH;
export const CEO_MAP_DISPLAY_HEIGHT = CTO_PIN_DISPLAY_HEIGHT * CEO_MAP_HEIGHT_SCALE;

const DEFAULT_CAIXA_COLOR = '#FF0000';

function normalizeHexColor(color?: string | null): string {
  if (color && /^#[0-9A-Fa-f]{3,8}$/.test(color.trim())) return color.trim();
  return DEFAULT_CAIXA_COLOR;
}

export function getMapPinLabelOrigin(displayWidth: number, displayHeight: number): { x: number; y: number } {
  return {
    x: MAP_PIN_LABEL_CENTER_X * (displayWidth / MAP_PIN_VIEW_WIDTH),
    y: MAP_PIN_LABEL_CENTER_Y * (displayHeight / MAP_PIN_VIEW_HEIGHT),
  };
}

function buildCoilRings(c: string): string {
  return (
    `<circle cx="15.5" cy="14" r="4.2" fill="none" stroke="${c}" stroke-width="1.5"/>` +
    `<circle cx="19.5" cy="16.5" r="4.2" fill="none" stroke="${c}" stroke-width="1.5"/>` +
    `<circle cx="23.5" cy="19" r="4.2" fill="none" stroke="${c}" stroke-width="1.5"/>`
  );
}

export function buildMapPinSvg(color: string, options?: { showCoilRings?: boolean }): string {
  const showCoilRings = options?.showCoilRings ?? false;
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${MAP_PIN_VIEW_WIDTH} ${MAP_PIN_VIEW_HEIGHT}" width="${MAP_PIN_VIEW_WIDTH}" height="${MAP_PIN_VIEW_HEIGHT}">` +
    `<path d="M20 1 C10.3 1 2.5 9.2 2.5 19.3 C2.5 30 20 47.5 20 47.5 C20 47.5 37.5 30 37.5 19.3 C37.5 9.2 29.7 1 20 1 Z" fill="${color}" stroke="#ffffff" stroke-width="2" stroke-linejoin="round"/>` +
    `<circle cx="20" cy="17.5" r="13.5" fill="#ffffff"/>` +
    (showCoilRings ? buildCoilRings(color) : '') +
    `</svg>`
  );
}

function buildCeoIconSvg(c: string): string {
  return (
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 200" width="50" height="100">' +
    '<path d="M20 160 V 45 C 20 25, 80 25, 80 45 V 160 H 84 V 180 H 76 V 195 H 66 V 180 H 34 V 195 H 24 V 180 H 16 V 160 H 20 Z" fill="' + c + '"/>' +
    '<rect x="16" y="60" width="68" height="6" rx="3" ry="3" fill="' + c + '"/>' +
    '<rect x="16" y="85" width="68" height="6" rx="3" ry="3" fill="' + c + '"/>' +
    '<rect x="16" y="110" width="68" height="6" rx="3" ry="3" fill="' + c + '"/>' +
    '<rect x="16" y="135" width="68" height="6" rx="3" ry="3" fill="' + c + '"/>' +
    '</svg>'
  );
}

export function getCtoIconDataUrl(color?: string | null): string {
  const c = normalizeHexColor(color);
  return 'data:image/svg+xml,' + encodeURIComponent(buildMapPinSvg(c, { showCoilRings: false }));
}

export function getCeoIconDataUrl(color?: string | null): string {
  const c = normalizeHexColor(color);
  return 'data:image/svg+xml,' + encodeURIComponent(buildCeoIconSvg(c));
}

/** Anchor no mapa — base da caixa CEO (viewBox 100×200). */
export function getCeoMapIconAnchor(displayWidth: number, displayHeight: number): { x: number; y: number } {
  return {
    x: displayWidth * 0.5,
    y: displayHeight * (195 / 200),
  };
}

/** Fonte do número de portas no pin CTO (viewBox 40×48). */
export const MAP_PIN_PORT_FONT_SIZE = 19;

/** Cor do número de portas no pin CTO. */
export const MAP_PIN_PORT_TEXT_COLOR = '#000000';

/** Cor de destaque para caixas aguardando instalação no mapa. */
export const AGUARDANDO_INSTALACAO_MAP_COLOR = '#FF9800';

function decodeSvgDataUrl(svgDataUrl: string): string | null {
  try {
    const encoded = svgDataUrl.replace(/^data:image\/svg\+xml(?:;charset=UTF-8)?,/, '');
    return decodeURIComponent(encoded);
  } catch {
    return null;
  }
}

/** Borda tracejada laranja ao redor do marcador (status aguardando instalação). */
export function addAguardandoInstalacaoBorderToSvg(
  svgDataUrl: string,
  color: string = AGUARDANDO_INSTALACAO_MAP_COLOR,
): string {
  const svgString = decodeSvgDataUrl(svgDataUrl);
  if (!svgString) return svgDataUrl;

  const viewBoxMatch = svgString.match(/viewBox=["']([^"']+)["']/);
  if (!viewBoxMatch) return svgDataUrl;

  const parts = viewBoxMatch[1].trim().split(/[\s,]+/).map(Number);
  if (parts.length !== 4 || !parts.every(n => Number.isFinite(n))) return svgDataUrl;

  const [vx, vy, vw, vh] = parts;
  const stroke = Math.max(2, Math.min(vw, vh) * 0.05);
  const safeColor = /^#[0-9A-Fa-f]{3,8}$/.test(color) ? color : AGUARDANDO_INSTALACAO_MAP_COLOR;

  const closeIndex = svgString.lastIndexOf('</svg>');
  if (closeIndex === -1) return svgDataUrl;

  const border =
    `<rect x="${vx + stroke / 2}" y="${vy + stroke / 2}" ` +
    `width="${vw - stroke}" height="${vh - stroke}" ` +
    `fill="none" stroke="${safeColor}" stroke-width="${stroke}" stroke-dasharray="6 4" rx="${stroke * 2}" ry="${stroke * 2}"/>`;

  const updated = `${svgString.slice(0, closeIndex)}${border}${svgString.slice(closeIndex)}`;
  return 'data:image/svg+xml,' + encodeURIComponent(updated);
}

/** Número de portas no centro da bola branca (pin CTO). */
export function addPortTextToPinSvg(
  svgDataUrl: string,
  portText: string,
  color: string = MAP_PIN_PORT_TEXT_COLOR,
): string {
  const encoded = svgDataUrl.replace(/^data:image\/svg\+xml(?:;charset=UTF-8)?,/, '');
  let svgString: string;
  try {
    svgString = decodeURIComponent(encoded);
  } catch {
    return svgDataUrl;
  }

  const safeColor = /^#[0-9A-Fa-f]{3,8}$/.test(color) ? color : DEFAULT_CAIXA_COLOR;
  const safeText = portText.replace(/[<>&]/g, '');
  const portLabel =
    `<text x="${MAP_PIN_LABEL_CENTER_X}" y="${MAP_PIN_LABEL_CENTER_Y}" ` +
    `fill="${safeColor}" font-family="Arial,Helvetica,sans-serif" font-size="${MAP_PIN_PORT_FONT_SIZE}" font-weight="bold" ` +
    `text-anchor="middle" dominant-baseline="middle">${safeText}</text>`;

  const closeIndex = svgString.lastIndexOf('</svg>');
  if (closeIndex === -1) return svgDataUrl;

  const updated = `${svgString.slice(0, closeIndex)}${portLabel}${svgString.slice(closeIndex)}`;
  return 'data:image/svg+xml,' + encodeURIComponent(updated);
}
